import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { findFarmsByProximity } from "../../api/search-service/searchService"
import useUserLocation from "../../hooks/useUserLocation"

import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";
import SkeletonCard  from "../common/SkeletonCard"
import SwiperFarmCardList from "./SwiperFarmCardList";
import CarouselNavigation from "../common/CarouselNavigation";
import slugify from 'slugify';
import logger from "../../utils/logger";

const log = logger.create("TrendingCarousel");

const trendingCarouselContainerTitle = "Top picks this week";
const trendingCarouselContainerSubtitle = "This week’s most visited and loved farms";

/**
 * TrendingCarousel component displays a carousel of trending farms based on user location.
 * It fetches farms by proximity and displays them in a swiper carousel.
 * @returns {JSX.Element} The rendered component.
 */
function TrendingCarousel() {
  const navigate                      = useNavigate();
  const swiperRef                     = useRef(null);
  const prevRef                       = useRef(null);
  const nextRef                       = useRef(null);
  const { location, error }           = useUserLocation()
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd]             = useState(false);
  const [farms, setFarms]             = useState([]);
  const [loading, setLoading]         = useState(true); 

  const DEFAULT_VERBOSE               = false;
  const DEFAULT_PROXIMITY_DISTANCE    = 10000; // 10,000 km radius
  const TRENDING_BANNER               = 'Trending'
  const DEFAULT_FARM_ARRAY_LENGTH     = 6;
  const DEFAULT_SORT                  = 'distance';
  const DEFAULT_SORT_ORDER            = 'ASC';

  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };


  // DEBUG: useUserLocation (IP/GPS) 
  useEffect(() => {
    if (!location) {
      log.log('[AnimalsCarousel][Location] null', { error });
      return;
    }
    const source = location?.source ?? (location?.ip ? 'IP' : 'GPS');
    log.log(`[AnimalsCarousel][Location] from ${source}:`, location);
  }, [location, error]); 
  
  
  // Fetch farms by proximity when location is available
  useEffect(() => {
      if (!location) return

      setLoading(true);     
      findFarmsByProximity({
        verbose:    DEFAULT_VERBOSE,
        latitude:   location.lat, 
        longitude:  location.lng,
        distance:   DEFAULT_PROXIMITY_DISTANCE,
        banner:     TRENDING_BANNER,
        limit:      DEFAULT_FARM_ARRAY_LENGTH,
        sort:       DEFAULT_SORT,
        order:      DEFAULT_SORT_ORDER
      })
        .then(response => {
          const farmsArray = Array.isArray(response.items) ? response.items : [];
          log.log("Proximity result Trending:", farmsArray);
          setFarms(farmsArray)      
        })
        .catch((err) => {
          log.error("Error fetching trending farms:", err);
        })
        .finally(() => setLoading(false)); 
  }, [location])

  // Update swiper navigation state on slide change
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    const onChange = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    swiper.on("slideChange", onChange);
    onChange();
    return () => swiper.off("slideChange", onChange);
  }, [farms]);

  // Handle navigation button state
  if (error) return <div className="p-8 text-center">Location error: {error}</div>

  return (
    <section className={FarmCardStyle["events-section"]}>
      <div className={FarmCardStyle["events-container"]}>
        <div className={FarmCardStyle["events-header"]}>
          <div>
            {loading
              ? <SkeletonCard 
                  variant="header" 
                  titleClass={FarmCardStyle["events-title"]}
                  subtitleClass={FarmCardStyle["events-subtitle"]}
                />
              : (
                <>
                  <h2 className={FarmCardStyle["events-title"]}>
                    {trendingCarouselContainerTitle} &#62;
                  </h2>
                  <p className={FarmCardStyle["events-subtitle"]}>
                    {trendingCarouselContainerSubtitle}
                  </p>
                </>
              )
            }
          </div>
          <CarouselNavigation 
            prevRef={prevRef}
            nextRef={nextRef}
            isBeginning={isBeginning}
            isEnd={isEnd}
          />
        </div>

        <SwiperFarmCardList 
          farms={farms}
          loading={loading}
          onFarmClick={farm => {
            const slug = slugify(farm.name || '', { lower: true, strict: true });
            navigate(`/farm/${farm.id}/${slug}`);
          }}          skeletonCount={6}
          prevRef={prevRef}
          nextRef={nextRef}
          updateNavState={updateNavState}
        />
      </div>
    </section>
  );
}

export default TrendingCarousel;